import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Upload, Pencil, Trash2, FileJson, Search, Filter, Eye, ChevronDown, ChevronUp, Play, Target, Wind, ArrowUpDown, CheckSquare, Square, X, Copy, FolderOpen, FolderPlus, MoreVertical, Edit, Check } from 'lucide-react';

function Entrenos() {
  const [entrenos, setEntrenos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingEntreno, setEditingEntreno] = useState(null);
  const [viewEntreno, setViewEntreno] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPanteon, setFilterPanteon] = useState('todos');
  const [filterTipo, setFilterTipo] = useState('todos');
  const [jsonInput, setJsonInput] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [ordenFecha, setOrdenFecha] = useState('desc'); // 'asc' o 'desc'
  const [selectedEntrenos, setSelectedEntrenos] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);

  // Carpetas
  const [carpetas, setCarpetas] = useState([]);
  const [activeCarpeta, setActiveCarpeta] = useState('todas'); // 'todas' | 'sin_clasificar' | carpeta_id
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderMenuId, setFolderMenuId] = useState(null);
  const [draggingEntrenoId, setDraggingEntrenoId] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [manageFoldersEntreno, setManageFoldersEntreno] = useState(null);
  
  const [formData, setFormData] = useState({
    fecha: '',
    codigo: '',
    dios: '',
    panteon: '',
    intensidad: 5,
    tipo: 'funcional',
    puesta_en_marcha: '',
    entreno_principal: '',
    vuelta_a_la_calma: '',
    descripcion: '',
    color: ''
  });

  const panteones = ['Griego', 'Romano', 'Nórdico', 'Egipcio', 'Celta', 'Japonés', 'Hindú', 'Azteca', 'Maya', 'Otro'];
  const tiposEntreno = [
    { value: 'carrera', label: 'Carrera', color: 'bg-blue-500', textColor: 'text-blue-400' },
    { value: 'funcional', label: 'Funcional', color: 'bg-yellow-500', textColor: 'text-yellow-400' },
    { value: 'competicion', label: 'Competición', color: 'bg-red-500', textColor: 'text-red-400' },
    { value: 'especial', label: 'Especial', color: 'bg-purple-500', textColor: 'text-purple-400' },
    { value: 'excursion', label: 'Excursión', color: 'bg-green-500', textColor: 'text-green-400' },
    { value: 'natacion', label: 'Natación', color: 'bg-cyan-500', textColor: 'text-cyan-400' },
    { value: 'bike', label: 'Bike', color: 'bg-orange-500', textColor: 'text-orange-400' }
  ];

  useEffect(() => {
    fetchEntrenos();
  }, []);

  const fetchEntrenos = async () => {
    try {
      const [resEntrenos, resCarpetas] = await Promise.all([
        axios.get(`${API}/entrenos`),
        axios.get(`${API}/carpetas-entrenos`).catch(() => ({ data: [] })),
      ]);
      setEntrenos(resEntrenos.data);
      setCarpetas(resCarpetas.data || []);
    } catch (error) {
      console.error('Error al cargar entrenos:', error);
    } finally {
      setLoading(false);
    }
  };

  // ---- CARPETAS ----
  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await axios.post(`${API}/carpetas-entrenos`, { nombre: newFolderName.trim() });
      setCarpetas(prev => [...prev, res.data]);
      setNewFolderName('');
      setShowNewFolder(false);
    } catch (e) { alert(e.response?.data?.detail || 'Error al crear carpeta'); }
  };

  const renameFolder = async (id, nombre) => {
    try {
      const res = await axios.put(`${API}/carpetas-entrenos/${id}`, { nombre });
      setCarpetas(prev => prev.map(c => c.id === id ? res.data : c));
      setEditingFolder(null);
    } catch (e) { alert('Error al renombrar'); }
  };

  const deleteFolder = async (id) => {
    if (!window.confirm('¿Eliminar carpeta? Los entrenos no se borran, solo pierden esta etiqueta.')) return;
    try {
      await axios.delete(`${API}/carpetas-entrenos/${id}`);
      setCarpetas(prev => prev.filter(c => c.id !== id));
      setEntrenos(prev => prev.map(e => ({ ...e, carpetas: (e.carpetas || []).filter(c => c !== id) })));
      if (activeCarpeta === id) setActiveCarpeta('todas');
      setFolderMenuId(null);
    } catch (e) { alert('Error al eliminar'); }
  };

  const toggleEntrenoFolder = async (entrenoId, folderId) => {
    const entreno = entrenos.find(e => e.id === entrenoId);
    if (!entreno) return;
    const current = entreno.carpetas || [];
    const newCarpetas = current.includes(folderId) ? current.filter(c => c !== folderId) : [...current, folderId];
    try {
      await axios.put(`${API}/entrenos/${entrenoId}/carpetas`, { carpetas: newCarpetas });
      setEntrenos(prev => prev.map(e => e.id === entrenoId ? { ...e, carpetas: newCarpetas } : e));
    } catch (e) { alert('Error'); }
  };

  const addEntrenoToFolder = async (entrenoId, folderId) => {
    const entreno = entrenos.find(e => e.id === entrenoId);
    if (!entreno) return;
    const current = entreno.carpetas || [];
    if (current.includes(folderId)) return;
    const newCarpetas = [...current, folderId];
    try {
      await axios.put(`${API}/entrenos/${entrenoId}/carpetas`, { carpetas: newCarpetas });
      setEntrenos(prev => prev.map(e => e.id === entrenoId ? { ...e, carpetas: newCarpetas } : e));
    } catch (e) { alert('Error'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntreno) {
        await axios.put(`${API}/entrenos/${editingEntreno.id}`, formData);
        alert('Entreno actualizado correctamente');
      } else {
        await axios.post(`${API}/entrenos`, formData);
        alert('Entreno creado correctamente');
      }
      fetchEntrenos();
      resetForm();
      setDialogOpen(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al guardar entreno');
    }
  };

  const handleEdit = (entreno) => {
    setEditingEntreno(entreno);
    setFormData({
      fecha: entreno.fecha,
      codigo: entreno.codigo,
      dios: entreno.dios,
      panteon: entreno.panteon,
      intensidad: entreno.intensidad,
      tipo: entreno.tipo || 'funcional',
      puesta_en_marcha: entreno.puesta_en_marcha || '',
      entreno_principal: entreno.entreno_principal || '',
      vuelta_a_la_calma: entreno.vuelta_a_la_calma || '',
      descripcion: entreno.descripcion || '',
      color: entreno.color || ''
    });
    setDialogOpen(true);
  };

  const handleDuplicate = (entreno) => {
    // Abre el formulario en modo "crear" pre-rellenado con los datos del entreno,
    // sugiriendo un código nuevo "X (copia)" y la fecha de hoy
    setEditingEntreno(null);
    const hoy = new Date().toISOString().slice(0, 10);
    setFormData({
      fecha: hoy,
      codigo: `${entreno.codigo} (copia)`,
      dios: entreno.dios || '',
      panteon: entreno.panteon || '',
      intensidad: entreno.intensidad ?? 5,
      tipo: entreno.tipo || 'funcional',
      puesta_en_marcha: entreno.puesta_en_marcha || '',
      entreno_principal: entreno.entreno_principal || '',
      vuelta_a_la_calma: entreno.vuelta_a_la_calma || '',
      descripcion: entreno.descripcion || '',
      color: entreno.color || ''
    });
    setDialogOpen(true);
  };

  const handleView = (entreno) => {
    setViewEntreno(entreno);
    setViewDialogOpen(true);
  };

  const handleDelete = async (entrenoId, codigo) => {
    if (window.confirm(`¿Estás seguro de eliminar el entreno "${codigo}"?`)) {
      try {
        await axios.delete(`${API}/entrenos/${entrenoId}`);
        fetchEntrenos();
        alert('Entreno eliminado correctamente');
      } catch (error) {
        alert('Error al eliminar entreno');
      }
    }
  };

  const handleDeleteAll = async () => {
    const count = entrenos.length;
    if (count === 0) {
      alert('No hay entrenos para eliminar');
      return;
    }
    
    if (window.confirm(`⚠️ ATENCIÓN: ¿Estás seguro de eliminar TODOS los ${count} entrenos?\n\nEsta acción no se puede deshacer.`)) {
      if (window.confirm(`🚨 CONFIRMACIÓN FINAL: Se eliminarán ${count} entrenos permanentemente.\n\n¿Continuar?`)) {
        try {
          await axios.delete(`${API}/entrenos`);
          fetchEntrenos();
          alert(`Se eliminaron ${count} entrenos correctamente`);
        } catch (error) {
          alert('Error al eliminar entrenos');
        }
      }
    }
  };

  const handleImport = async () => {
    try {
      const jsonData = JSON.parse(jsonInput);
      const entrenos = Array.isArray(jsonData) ? jsonData : jsonData.entrenos || [jsonData];
      
      const response = await axios.post(`${API}/entrenos/import`, { entrenos });
      setImportResult(response.data);
      fetchEntrenos();
      
      if (response.data.imported > 0) {
        setJsonInput('');
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        setImportResult({ error: 'JSON inválido. Verifica el formato.' });
      } else {
        setImportResult({ error: error.response?.data?.detail || 'Error al importar' });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      fecha: '',
      codigo: '',
      dios: '',
      panteon: '',
      intensidad: 5,
      tipo: 'funcional',
      puesta_en_marcha: '',
      entreno_principal: '',
      vuelta_a_la_calma: '',
      descripcion: '',
      color: ''
    });
    setEditingEntreno(null);
  };

  const getTipoInfo = (tipo) => {
    const found = tiposEntreno.find(t => t.value === tipo);
    return found || { value: tipo, label: tipo, color: 'bg-gray-500', textColor: 'text-gray-400' };
  };

  // Devuelve props para el chip del tipo: si el entreno tiene color custom (solo
  // aplicable a 'especial'), aplica style inline; si no, usa la clase Tailwind por defecto.
  const getTipoChipProps = (entreno) => {
    const info = getTipoInfo(entreno?.tipo);
    if (entreno?.color && entreno?.tipo === 'especial') {
      return { className: '', style: { backgroundColor: entreno.color }, label: info.label };
    }
    return { className: info.color, style: {}, label: info.label };
  };

  // Paleta sugerida para "Especial"
  const ESPECIAL_PRESETS = ['#a855f7', '#ec4899', '#f59e0b', '#22d3ee', '#10b981', '#ef4444', '#6366f1', '#94a3b8'];

  const getIntensidadColor = (intensidad) => {
    if (intensidad <= 3) return 'bg-green-500';
    if (intensidad <= 5) return 'bg-yellow-500';
    if (intensidad <= 7) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getPanteonColor = (panteon) => {
    const colors = {
      'Griego': 'text-blue-400',
      'Romano': 'text-purple-400',
      'Nórdico': 'text-cyan-400',
      'Egipcio': 'text-yellow-400',
      'Celta': 'text-green-400',
      'Japonés': 'text-red-400',
      'Hindú': 'text-orange-400',
      'Azteca': 'text-amber-400',
      'Maya': 'text-teal-400',
    };
    return colors[panteon] || 'text-gray-400';
  };

  const toggleCardExpand = (entrenoId) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(entrenoId)) {
      newExpanded.delete(entrenoId);
    } else {
      newExpanded.add(entrenoId);
    }
    setExpandedCards(newExpanded);
  };

  // Funciones de selección múltiple
  const toggleSelectEntreno = (entrenoId) => {
    const newSelected = new Set(selectedEntrenos);
    if (newSelected.has(entrenoId)) {
      newSelected.delete(entrenoId);
    } else {
      newSelected.add(entrenoId);
    }
    setSelectedEntrenos(newSelected);
  };

  const selectAll = () => {
    const allIds = new Set(filteredEntrenos.map(e => e.id));
    setSelectedEntrenos(allIds);
  };

  const deselectAll = () => {
    setSelectedEntrenos(new Set());
  };

  const deleteSelected = () => {
    const count = selectedEntrenos.size;
    if (count === 0) {
      alert('No hay entrenos seleccionados');
      return;
    }
    if (window.confirm(`¿Eliminar ${count} entreno(s) seleccionado(s)?`)) {
      const ids = Array.from(selectedEntrenos);
      Promise.all(ids.map(id => axios.delete(`${API}/entrenos/${id}`)))
        .then(() => {
          alert(`${count} entreno(s) eliminado(s)`);
          setSelectedEntrenos(new Set());
          setSelectionMode(false);
          fetchEntrenos();
        })
        .catch(() => alert('Error al eliminar'));
    }
  };

  // Función para parsear fecha en formato DD/MM/YYYY o YYYY-MM-DD
  const parseFecha = (fechaStr) => {
    if (!fechaStr) return new Date(0);
    
    // Si contiene '/' es formato DD/MM/YYYY
    if (fechaStr.includes('/')) {
      const [dia, mes, año] = fechaStr.split('/');
      return new Date(año, mes - 1, dia);
    }
    // Si contiene '-' es formato YYYY-MM-DD
    if (fechaStr.includes('-')) {
      return new Date(fechaStr);
    }
    return new Date(0);
  };

  // Filtrar y ordenar entrenos
  const filteredEntrenos = entrenos
    .filter(entreno => {
      const matchesSearch = 
        entreno.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entreno.dios.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPanteon = filterPanteon === 'todos' || entreno.panteon === filterPanteon;
      const matchesTipo = filterTipo === 'todos' || entreno.tipo === filterTipo;
      const matchesCarpeta = activeCarpeta === 'todas'
        ? true
        : activeCarpeta === 'sin_clasificar'
          ? !(entreno.carpetas?.length)
          : (entreno.carpetas || []).includes(activeCarpeta);
      return matchesSearch && matchesPanteon && matchesTipo && matchesCarpeta;
    })
    .sort((a, b) => {
      const fechaA = parseFecha(a.fecha);
      const fechaB = parseFecha(b.fecha);
      if (ordenFecha === 'asc') {
        return fechaA - fechaB;
      } else {
        return fechaB - fechaA;
      }
    });

  const uniquePanteones = [...new Set(entrenos.map(e => e.panteon))];
  const uniqueTipos = [...new Set(entrenos.map(e => e.tipo).filter(Boolean))];

  const exampleJson = `[
  {
    "fecha": "2026-01-15",
    "codigo": "ENT-001",
    "dios": "Zeus",
    "panteon": "Griego",
    "intensidad": 8,
    "tipo": "funcional",
    "puesta_en_marcha": "10 min trote suave + movilidad",
    "entreno_principal": "5x5 sentadillas + 3x10 press",
    "vuelta_a_la_calma": "Estiramientos 10 min"
  }
]`;

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gold mb-2" data-testid="entrenos-title">
              Base de Entrenos
            </h1>
            <p className="text-gray-400">Gestiona tu biblioteca de entrenamientos</p>
            <p className="text-gray-500 text-sm">{entrenos.length} entrenos en la base de datos</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Botón Ordenar por Fecha */}
            <Button 
              variant="outline" 
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
              onClick={() => setOrdenFecha(ordenFecha === 'desc' ? 'asc' : 'desc')}
            >
              <ArrowUpDown className="mr-2 h-4 w-4" />
              Fecha {ordenFecha === 'desc' ? '↓ Reciente' : '↑ Antiguo'}
            </Button>

            {/* Botón Modo Selección */}
            {entrenos.length > 0 && (
              <Button 
                variant="outline" 
                className={selectionMode ? "border-gold text-gold hover:bg-gold/20" : "border-gray-600 text-gray-300 hover:bg-gray-700"}
                onClick={() => {
                  setSelectionMode(!selectionMode);
                  if (selectionMode) {
                    setSelectedEntrenos(new Set());
                  }
                }}
              >
                {selectionMode ? <X className="mr-2 h-4 w-4" /> : <CheckSquare className="mr-2 h-4 w-4" />}
                {selectionMode ? 'Cancelar' : 'Seleccionar'}
              </Button>
            )}

            {/* Botones de selección cuando está activo */}
            {selectionMode && (
              <>
                <Button 
                  variant="outline" 
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  onClick={selectAll}
                >
                  Seleccionar Todos
                </Button>
                <Button 
                  variant="outline" 
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  onClick={deselectAll}
                >
                  Deseleccionar
                </Button>
                {selectedEntrenos.size > 0 && (
                  <Button 
                    variant="outline" 
                    className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                    onClick={deleteSelected}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar ({selectedEntrenos.size})
                  </Button>
                )}
              </>
            )}

            {/* Botón Borrar Todos */}
            {entrenos.length > 0 && !selectionMode && (
              <Button 
                variant="outline" 
                className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                onClick={() => {
                  const count = entrenos.length;
                  if (window.confirm(`¿Eliminar TODOS los ${count} entrenos?`)) {
                    if (window.confirm(`CONFIRMACIÓN: Se eliminarán ${count} entrenos. ¿Continuar?`)) {
                      axios.delete(`${API}/entrenos`)
                        .then(() => {
                          fetchEntrenos();
                          alert(`${count} entrenos eliminados`);
                        })
                        .catch(() => alert('Error al eliminar'));
                    }
                  }
                }}
                data-testid="btn-delete-all"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Borrar Todos
              </Button>
            )}
            
            {/* Botón Importar JSON */}
            <Dialog open={importDialogOpen} onOpenChange={(open) => {
              setImportDialogOpen(open);
              if (!open) setImportResult(null);
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-gold text-gold hover:bg-gold/10" data-testid="btn-import">
                  <Upload className="mr-2 h-4 w-4" />
                  Importar JSON
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-gold flex items-center gap-2">
                    <FileJson className="h-5 w-5" />
                    Importar Entrenos desde JSON
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Pega tu JSON aquí:</Label>
                    <Textarea
                      value={jsonInput}
                      onChange={(e) => setJsonInput(e.target.value)}
                      placeholder={exampleJson}
                      className="bg-gray-800 border-gray-700 min-h-[200px] font-mono text-sm"
                      data-testid="json-input"
                    />
                  </div>
                  
                  <div className="bg-gray-800 rounded-lg p-4">
                    <Label className="text-sm text-gray-400">Campos requeridos:</Label>
                    <pre className="text-xs text-gray-500 mt-2 overflow-x-auto whitespace-pre-wrap">
{`fecha, codigo, dios, panteon, intensidad (1-10),
tipo (carrera/funcional/competicion/especial/excursion),
puesta_en_marcha, entreno_principal, vuelta_a_la_calma

Opcional: descripcion`}
                    </pre>
                    <div className="mt-3 text-xs text-gray-400">
                      <span className="text-gold">Ejemplo:</span>
                      <pre className="mt-1 bg-gray-900 p-2 rounded overflow-x-auto">
{`{
  "fecha": "2026-01-15",
  "codigo": "MK-001",
  "dios": "Zeus",
  "panteon": "Griego",
  "intensidad": 8,
  "tipo": "funcional",
  "puesta_en_marcha": "Trote 10 min",
  "entreno_principal": "5x5 sentadillas",
  "vuelta_a_la_calma": "Estiramientos"
}`}
                      </pre>
                    </div>
                  </div>

                  {importResult && (
                    <div className={`p-4 rounded-lg ${importResult.error ? 'bg-red-900/30 border border-red-600' : 'bg-green-900/30 border border-green-600'}`}>
                      {importResult.error ? (
                        <p className="text-red-400">{importResult.error}</p>
                      ) : (
                        <div className="space-y-1">
                          <p className="text-green-400 font-medium">{importResult.message}</p>
                          <p className="text-sm text-gray-300">Importados: {importResult.imported}</p>
                          <p className="text-sm text-gray-300">Omitidos: {importResult.skipped}</p>
                          {importResult.errors?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-sm text-red-400">Errores:</p>
                              {importResult.errors.map((err, i) => (
                                <p key={i} className="text-xs text-red-300">{err}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    onClick={handleImport}
                    className="w-full btn-primary-mk"
                    disabled={!jsonInput.trim()}
                    data-testid="btn-confirm-import"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Importar Entrenos
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Botón Nuevo Entreno */}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary-mk" data-testid="btn-add-entreno">
                  <Plus className="mr-2 h-4 w-4" /> Nuevo Entreno
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-gold">
                    {editingEntreno ? 'Editar Entreno' : 'Crear Nuevo Entreno'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Fila 1: Fecha, Código */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fecha">Fecha</Label>
                      <Input
                        id="fecha"
                        type="date"
                        value={formData.fecha}
                        onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                        required
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid="input-fecha"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="codigo">Código</Label>
                      <Input
                        id="codigo"
                        value={formData.codigo}
                        onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                        placeholder="ENT-001"
                        required
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid="input-codigo"
                      />
                    </div>
                  </div>
                  
                  {/* Fila 2: Dios, Panteón */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="dios">Dios</Label>
                      <Input
                        id="dios"
                        value={formData.dios}
                        onChange={(e) => setFormData({ ...formData, dios: e.target.value })}
                        placeholder="Zeus, Thor, Anubis..."
                        required
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid="input-dios"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="panteon">Panteón</Label>
                      <Select
                        value={formData.panteon}
                        onValueChange={(value) => setFormData({ ...formData, panteon: value })}
                      >
                        <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="select-panteon">
                          <SelectValue placeholder="Seleccionar panteón" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700 text-white">
                          {panteones.map(p => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Fila 3: Tipo, Intensidad */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tipo">Tipo de Entreno</Label>
                      <Select
                        value={formData.tipo}
                        onValueChange={(value) => setFormData({ ...formData, tipo: value, color: value === 'especial' ? (formData.color || '') : '' })}
                      >
                        <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="select-tipo">
                          <SelectValue placeholder="Seleccionar tipo" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700 text-white">
                          {tiposEntreno.map(t => (
                            <SelectItem key={t.value} value={t.value}>
                              <span className="flex items-center gap-2">
                                <span className={`w-3 h-3 rounded-full ${t.color}`}></span>
                                {t.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.tipo === 'especial' && (
                        <div className="mt-2 p-2 bg-gray-800/60 rounded-lg border border-gray-700 space-y-2" data-testid="especial-color-picker">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-gray-400">Color del badge</Label>
                            {formData.color && (
                              <button type="button" onClick={() => setFormData({ ...formData, color: '' })}
                                className="text-[10px] text-gray-500 hover:text-white underline"
                                data-testid="btn-color-reset">Restablecer</button>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {ESPECIAL_PRESETS.map(c => (
                              <button key={c} type="button"
                                onClick={() => setFormData({ ...formData, color: c })}
                                className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                                  (formData.color || '#a855f7') === c ? 'border-white ring-2 ring-white/40 scale-110' : 'border-gray-600'
                                }`}
                                style={{ backgroundColor: c }}
                                title={c}
                                data-testid={`color-preset-${c.replace('#','')}`}
                              />
                            ))}
                            <input type="color"
                              value={formData.color || '#a855f7'}
                              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                              className="w-7 h-7 rounded-full bg-transparent border border-gray-600 cursor-pointer p-0"
                              data-testid="input-color-custom"
                              title="Color personalizado"
                            />
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <span className="text-[10px] text-gray-500 uppercase tracking-wider">Vista previa:</span>
                            <span className="text-xs px-2 py-1 rounded-full text-white font-medium"
                              style={{ backgroundColor: formData.color || '#a855f7' }}
                              data-testid="color-preview">
                              Especial
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Intensidad: {formData.intensidad}/10</Label>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={formData.intensidad}
                        onChange={(e) => setFormData({ ...formData, intensidad: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-gold"
                        data-testid="input-intensidad"
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Suave</span>
                        <span>Moderado</span>
                        <span>Intenso</span>
                      </div>
                    </div>
                  </div>

                  {/* Puesta en Marcha */}
                  <div className="space-y-2">
                    <Label htmlFor="puesta_en_marcha" className="flex items-center gap-2">
                      <Play className="h-4 w-4 text-green-400" />
                      Puesta en Marcha (Calentamiento)
                    </Label>
                    <Textarea
                      id="puesta_en_marcha"
                      value={formData.puesta_en_marcha}
                      onChange={(e) => setFormData({ ...formData, puesta_en_marcha: e.target.value })}
                      placeholder="Ej: 10 min trote suave + movilidad articular..."
                      required
                      className="bg-gray-800 border-gray-700 min-h-[80px]"
                      data-testid="input-puesta-en-marcha"
                    />
                  </div>

                  {/* Entreno Principal */}
                  <div className="space-y-2">
                    <Label htmlFor="entreno_principal" className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-gold" />
                      Entreno Principal
                    </Label>
                    <Textarea
                      id="entreno_principal"
                      value={formData.entreno_principal}
                      onChange={(e) => setFormData({ ...formData, entreno_principal: e.target.value })}
                      placeholder="Ej: 5x5 sentadillas + 3x10 press militar..."
                      required
                      className="bg-gray-800 border-gray-700 min-h-[100px]"
                      data-testid="input-entreno-principal"
                    />
                  </div>

                  {/* Vuelta a la Calma */}
                  <div className="space-y-2">
                    <Label htmlFor="vuelta_a_la_calma" className="flex items-center gap-2">
                      <Wind className="h-4 w-4 text-blue-400" />
                      Vuelta a la Calma (Enfriamiento)
                    </Label>
                    <Textarea
                      id="vuelta_a_la_calma"
                      value={formData.vuelta_a_la_calma}
                      onChange={(e) => setFormData({ ...formData, vuelta_a_la_calma: e.target.value })}
                      placeholder="Ej: Estiramientos 10 min + respiración..."
                      required
                      className="bg-gray-800 border-gray-700 min-h-[80px]"
                      data-testid="input-vuelta-a-la-calma"
                    />
                  </div>

                  {/* Descripción (opcional) */}
                  <div className="space-y-2">
                    <Label htmlFor="descripcion">Descripción (opcional)</Label>
                    <Textarea
                      id="descripcion"
                      value={formData.descripcion}
                      onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                      placeholder="Notas adicionales sobre el entreno..."
                      className="bg-gray-800 border-gray-700 text-white"
                      data-testid="input-descripcion"
                    />
                  </div>

                  <Button type="submit" className="w-full btn-primary-mk" data-testid="btn-save-entreno">
                    {editingEntreno ? 'Guardar Cambios' : 'Crear Entreno'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar por código o dios..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-gray-800 border-gray-700"
              data-testid="search-input"
            />
          </div>
          <Select value={filterPanteon} onValueChange={setFilterPanteon}>
            <SelectTrigger className="w-full sm:w-48 bg-gray-800 border-gray-700" data-testid="filter-panteon">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Panteón" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white">
              <SelectItem value="todos">Todos los panteones</SelectItem>
              {uniquePanteones.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger className="w-full sm:w-48 bg-gray-800 border-gray-700" data-testid="filter-tipo">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white">
              <SelectItem value="todos">Todos los tipos</SelectItem>
              {tiposEntreno.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Carpetas (tabs) */}
        <div className="mb-6">
          {draggingEntrenoId && carpetas.length > 0 && (
            <p className="text-xs text-gold mb-1 animate-pulse" data-testid="drag-hint-entrenos">Suelta en una carpeta para organizar</p>
          )}
          <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide items-center" data-testid="folder-tabs-entrenos">
            <button onClick={() => setActiveCarpeta('todas')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                activeCarpeta === 'todas' ? 'bg-gold/20 text-gold border border-gold/40' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'
              }`} data-testid="folder-entrenos-todas">
              Todas ({entrenos.length})
            </button>
            <button onClick={() => setActiveCarpeta('sin_clasificar')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                activeCarpeta === 'sin_clasificar' ? 'bg-gray-600/30 text-white border border-gray-500' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'
              }`} data-testid="folder-entrenos-sin-clasificar">
              Sin clasificar ({entrenos.filter(e => !(e.carpetas?.length)).length})
            </button>
            {carpetas.map(f => (
              <div key={f.id} className="relative flex-shrink-0">
                <button onClick={() => setActiveCarpeta(f.id)}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverFolderId(f.id); }}
                  onDragLeave={() => setDragOverFolderId(null)}
                  onDrop={(e) => { e.preventDefault(); if (draggingEntrenoId) addEntrenoToFolder(draggingEntrenoId, f.id); setDragOverFolderId(null); setDraggingEntrenoId(null); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                    activeCarpeta === f.id ? 'text-white border' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'
                  } ${dragOverFolderId === f.id ? 'ring-2 ring-gold scale-110 bg-gold/20 !border-gold' : ''}`}
                  style={activeCarpeta === f.id && dragOverFolderId !== f.id ? { background: `${f.color}22`, borderColor: `${f.color}66`, color: f.color } : {}}
                  data-testid={`folder-entrenos-${f.id}`}
                >
                  <FolderOpen className="w-3 h-3" />
                  {editingFolder === f.id ? (
                    <input autoFocus value={f.nombre}
                      onChange={e => setCarpetas(prev => prev.map(c => c.id === f.id ? { ...c, nombre: e.target.value } : c))}
                      onBlur={() => renameFolder(f.id, f.nombre)}
                      onKeyDown={e => e.key === 'Enter' && renameFolder(f.id, f.nombre)}
                      className="bg-transparent border-none outline-none text-xs w-24"
                      onClick={e => e.stopPropagation()}
                    />
                  ) : (
                    <span>{f.nombre} ({entrenos.filter(e => (e.carpetas || []).includes(f.id)).length})</span>
                  )}
                  <span onClick={e => { e.stopPropagation(); setFolderMenuId(folderMenuId === f.id ? null : f.id); }}
                    className="text-gray-500 hover:text-white ml-0.5 cursor-pointer" data-testid={`folder-entrenos-menu-${f.id}`}>
                    <MoreVertical className="w-3 h-3" />
                  </span>
                </button>
                {folderMenuId === f.id && (
                  <div className="absolute top-full left-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl py-1 z-50 min-w-[120px]">
                    <button onClick={() => { setEditingFolder(f.id); setFolderMenuId(null); }}
                      className="w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-800 flex items-center gap-2">
                      <Edit className="w-3 h-3" /> Renombrar
                    </button>
                    <button onClick={() => deleteFolder(f.id)}
                      className="w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-gray-800 flex items-center gap-2">
                      <Trash2 className="w-3 h-3" /> Eliminar
                    </button>
                  </div>
                )}
              </div>
            ))}
            {showNewFolder ? (
              <div className="flex items-center gap-1 flex-shrink-0">
                <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && createFolder()}
                  placeholder="Nombre..."
                  className="bg-gray-800 border border-gray-600 rounded-lg px-2 py-1 text-xs text-white w-28 outline-none focus:border-gold"
                  data-testid="input-new-folder-entrenos"
                />
                <button onClick={createFolder} className="text-green-400 hover:text-green-300 p-1" data-testid="btn-save-folder-entrenos">
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => { setShowNewFolder(false); setNewFolderName(''); }} className="text-gray-500 hover:text-white p-1">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button onClick={() => setShowNewFolder(true)}
                className="px-2 py-1.5 rounded-lg text-xs text-gray-500 hover:text-gold bg-gray-800 border border-dashed border-gray-700 hover:border-gold/50 flex items-center gap-1 whitespace-nowrap transition-colors flex-shrink-0"
                data-testid="btn-new-folder-entrenos">
                <FolderPlus className="w-3 h-3" /> Nueva
              </button>
            )}
          </div>
        </div>

        {/* Lista de Entrenos */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="loading-spinner"></div>
          </div>
        ) : filteredEntrenos.length === 0 ? (
          <div className="text-center py-20">
            <FileJson className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              {entrenos.length === 0 
                ? 'No hay entrenos en la base de datos' 
                : 'No se encontraron entrenos con esos filtros'}
            </p>
            <p className="text-gray-500 text-sm mt-2">
              {entrenos.length === 0 
                ? 'Crea uno nuevo o importa desde JSON' 
                : 'Prueba con otros términos de búsqueda'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEntrenos.map((entreno) => {
              const tipoInfo = getTipoInfo(entreno.tipo);
              const isExpanded = expandedCards.has(entreno.id);
              const isSelected = selectedEntrenos.has(entreno.id);
              
              return (
                <Card 
                  key={entreno.id} 
                  draggable={carpetas.length > 0 && !selectionMode}
                  onDragStart={(e) => { setDraggingEntrenoId(entreno.id); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragEnd={() => { setDraggingEntrenoId(null); setDragOverFolderId(null); }}
                  className={`card-mk hover:border-gold/50 transition-all ${isSelected ? 'border-gold border-2' : ''} ${carpetas.length > 0 && !selectionMode ? 'cursor-grab active:cursor-grabbing' : ''} ${draggingEntrenoId === entreno.id ? 'opacity-40 scale-95' : ''}`} 
                  data-testid={`entreno-card-${entreno.id}`}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white flex justify-between items-start">
                      <div className="flex items-start gap-3">
                        {/* Checkbox para selección */}
                        {selectionMode && (
                          <div 
                            className="cursor-pointer mt-1"
                            onClick={() => toggleSelectEntreno(entreno.id)}
                          >
                            {isSelected ? (
                              <CheckSquare className="h-5 w-5 text-gold" />
                            ) : (
                              <Square className="h-5 w-5 text-gray-500 hover:text-gold" />
                            )}
                          </div>
                        )}
                        <div>
                          <span className="text-lg font-bold">{entreno.dios}</span>
                          <div className={`text-sm font-normal ${getPanteonColor(entreno.panteon)}`}>
                            {entreno.panteon}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleView(entreno)}
                          className="h-8 w-8 text-gray-400 hover:text-blue-400 hover:bg-gray-700"
                          data-testid={`btn-view-${entreno.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(entreno)}
                          className="h-8 w-8 text-gray-400 hover:text-gold hover:bg-gray-700"
                          data-testid={`btn-edit-${entreno.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDuplicate(entreno)}
                          className="h-8 w-8 text-gray-400 hover:text-emerald-400 hover:bg-gray-700"
                          data-testid={`btn-duplicate-${entreno.id}`}
                          title="Duplicar entreno"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setManageFoldersEntreno(manageFoldersEntreno === entreno.id ? null : entreno.id)}
                          className={`h-8 w-8 hover:bg-gray-700 ${(entreno.carpetas || []).length > 0 ? 'text-gold hover:text-gold' : 'text-gray-400 hover:text-gold'}`}
                          data-testid={`btn-folders-${entreno.id}`}
                          title="Organizar en carpetas"
                        >
                          <FolderOpen className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (window.confirm(`¿Eliminar el entreno "${entreno.codigo}"?`)) {
                              axios.delete(`${API}/entrenos/${entreno.id}`)
                                .then(() => {
                                  fetchEntrenos();
                                  alert('Entreno eliminado');
                                })
                                .catch(() => alert('Error al eliminar'));
                            }
                          }}
                          className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-gray-700"
                          data-testid={`btn-delete-${entreno.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardTitle>
                    {/* Folder tags + management */}
                    {(() => {
                      const entrenoFolders = (entreno.carpetas || []).map(fid => carpetas.find(f => f.id === fid)).filter(Boolean);
                      return (
                        <>
                          {entrenoFolders.length > 0 && (
                            <div className="flex gap-1 mt-2 flex-wrap" data-testid={`folder-badges-${entreno.id}`}>
                              {entrenoFolders.map(f => (
                                <span key={f.id} className="text-[10px] px-1.5 py-0.5 rounded-md flex items-center gap-1" style={{ background: `${f.color}22`, color: f.color, border: `1px solid ${f.color}33` }}>
                                  <FolderOpen className="w-2.5 h-2.5" /> {f.nombre}
                                </span>
                              ))}
                            </div>
                          )}
                          {manageFoldersEntreno === entreno.id && (
                            <div className="mt-2 p-2 bg-gray-800 rounded-lg border border-gray-700" data-testid={`folder-manage-${entreno.id}`}>
                              {carpetas.length === 0 ? (
                                <p className="text-[11px] text-gray-500">Crea una carpeta primero con el botón <span className="text-gold">+ Nueva</span> de arriba.</p>
                              ) : (
                                <>
                                  <p className="text-[10px] text-gray-400 uppercase mb-1.5">Carpetas:</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {carpetas.map(f => {
                                      const isIn = (entreno.carpetas || []).includes(f.id);
                                      return (
                                        <button key={f.id} onClick={() => toggleEntrenoFolder(entreno.id, f.id)}
                                          className={`text-xs px-2 py-1 rounded-lg flex items-center gap-1 transition-colors ${
                                            isIn ? 'text-white' : 'text-gray-500 hover:text-white bg-gray-700'
                                          }`}
                                          style={isIn ? { background: `${f.color}33`, color: f.color, border: `1px solid ${f.color}55` } : {}}
                                          data-testid={`toggle-folder-${entreno.id}-${f.id}`}
                                        >
                                          {isIn ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />} {f.nombre}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Código, Fecha y Tipo */}
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-xs text-gray-500 font-mono bg-gray-800 px-2 py-1 rounded">
                        {entreno.codigo}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${getTipoChipProps(entreno).className} text-white font-medium`}
                        style={getTipoChipProps(entreno).style}
                        data-testid={`tipo-chip-${entreno.id}`}>
                        {tipoInfo.label}
                      </span>
                      <span className="text-xs text-gray-400">
                        {entreno.fecha}
                      </span>
                    </div>
                    
                    {/* Intensidad */}
                    <div className="space-y-1">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-400">Intensidad</span>
                        <span className="text-white font-bold">{entreno.intensidad}/10</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${getIntensidadColor(entreno.intensidad)}`}
                          style={{ width: `${entreno.intensidad * 10}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Botón expandir/contraer */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleCardExpand(entreno.id)}
                      className="w-full text-gray-400 hover:text-white hover:bg-gray-700"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="h-4 w-4 mr-2" />
                          Ocultar detalles
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4 mr-2" />
                          Ver detalles
                        </>
                      )}
                    </Button>

                    {/* Detalles expandidos */}
                    {isExpanded && (
                      <div className="space-y-3 pt-2 border-t border-gray-700">
                        {entreno.puesta_en_marcha && (
                          <div>
                            <div className="flex items-center gap-1 text-xs text-green-400 mb-1">
                              <Play className="h-3 w-3" /> Puesta en Marcha
                            </div>
                            <p className="text-xs text-gray-300 bg-gray-800/50 p-2 rounded whitespace-pre-wrap">
                              {entreno.puesta_en_marcha}
                            </p>
                          </div>
                        )}
                        {entreno.entreno_principal && (
                          <div>
                            <div className="flex items-center gap-1 text-xs text-gold mb-1">
                              <Target className="h-3 w-3" /> Entreno Principal
                            </div>
                            <p className="text-xs text-gray-300 bg-gray-800/50 p-2 rounded whitespace-pre-wrap">
                              {entreno.entreno_principal}
                            </p>
                          </div>
                        )}
                        {entreno.vuelta_a_la_calma && (
                          <div>
                            <div className="flex items-center gap-1 text-xs text-blue-400 mb-1">
                              <Wind className="h-3 w-3" /> Vuelta a la Calma
                            </div>
                            <p className="text-xs text-gray-300 bg-gray-800/50 p-2 rounded whitespace-pre-wrap">
                              {entreno.vuelta_a_la_calma}
                            </p>
                          </div>
                        )}
                        {entreno.descripcion && (
                          <p className="text-xs text-gray-400 italic whitespace-pre-wrap">
                            {entreno.descripcion}
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Diálogo de Ver Entreno Completo */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
            {viewEntreno && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-gold flex items-center justify-between">
                    <span className="text-2xl">{viewEntreno.dios}</span>
                    <span className={`text-sm px-3 py-1 rounded-full ${getTipoChipProps(viewEntreno).className} text-white`}
                      style={getTipoChipProps(viewEntreno).style}>
                      {getTipoInfo(viewEntreno.tipo).label}
                    </span>
                  </DialogTitle>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span className={getPanteonColor(viewEntreno.panteon)}>{viewEntreno.panteon}</span>
                    <span className="font-mono">{viewEntreno.codigo}</span>
                    <span>{viewEntreno.fecha}</span>
                  </div>
                </DialogHeader>
                
                <div className="space-y-6 mt-4">
                  {/* Intensidad */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Intensidad</span>
                      <span className="text-xl font-bold text-white">{viewEntreno.intensidad}/10</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${getIntensidadColor(viewEntreno.intensidad)}`}
                        style={{ width: `${viewEntreno.intensidad * 10}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Secciones del entreno */}
                  <div className="space-y-4">
                    <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-4">
                      <h3 className="flex items-center gap-2 text-green-400 font-medium mb-2">
                        <Play className="h-5 w-5" />
                        Puesta en Marcha
                      </h3>
                      <p className="text-gray-300 whitespace-pre-wrap">{viewEntreno.puesta_en_marcha || 'No especificado'}</p>
                    </div>

                    <div className="bg-gold/10 border border-gold/30 rounded-lg p-4">
                      <h3 className="flex items-center gap-2 text-gold font-medium mb-2">
                        <Target className="h-5 w-5" />
                        Entreno Principal
                      </h3>
                      <p className="text-gray-100 whitespace-pre-wrap">{viewEntreno.entreno_principal || 'No especificado'}</p>
                    </div>

                    <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                      <h3 className="flex items-center gap-2 text-blue-400 font-medium mb-2">
                        <Wind className="h-5 w-5" />
                        Vuelta a la Calma
                      </h3>
                      <p className="text-gray-300 whitespace-pre-wrap">{viewEntreno.vuelta_a_la_calma || 'No especificado'}</p>
                    </div>
                  </div>

                  {viewEntreno.descripcion && (
                    <div className="bg-gray-800 rounded-lg p-4">
                      <h3 className="text-gray-400 font-medium mb-2">Notas</h3>
                      <p className="text-gray-300 whitespace-pre-wrap">{viewEntreno.descripcion}</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default Entrenos;
